import { useEffect, useRef, useState } from 'react';

import { ViewportServiceClass } from '../../viewport-service';

/**
 * Custom hook to manage viewport state
 * @param viewportService - The viewport service instance
 */
export const useViewportState = (viewportService: ViewportServiceClass) => {
	const [viewportState, setViewportState] = useState(() => viewportService.state);
	const [isZooming, setIsZooming] = useState(() => viewportService.isZooming);
	const [isRotating, setIsRotating] = useState(() => viewportService.isRotating);
	const [isPanning, setIsPanning] = useState(() => viewportService.isPanning);
	const [containerSize, setContainerSize] = useState(() => viewportService.containerSize);
	const [viewerSize, setViewerSize] = useState(() => viewportService.viewerSize);

	// Add refs to track if we're currently updating to prevent infinite loops
	const isUpdating = useRef(false);

	// Ensure we have valid initial values
	useEffect(() => {
		// If container size is invalid (0 dimensions), try to get a valid size from the service
		if (!containerSize || containerSize.width <= 0 || containerSize.height <= 0) {
			const currentSize = viewportService.containerSize;
			if (currentSize && currentSize.width > 0 && currentSize.height > 0) {
				setContainerSize(currentSize);
			}
		}

		// If viewer size is invalid, try to get a valid size from the service
		if (!viewerSize || viewerSize.width <= 0 || viewerSize.height <= 0) {
			const currentViewerSize = viewportService.viewerSize;
			if (currentViewerSize && currentViewerSize.width > 0 && currentViewerSize.height > 0) {
				setViewerSize(currentViewerSize);
			}
		}
	}, [viewportService, containerSize, viewerSize]);

	useEffect(() => {
		// Subscribe to viewport state changes
		const unsubscribeState = viewportService.subscribe((newState) => {
			setViewportState(newState);
		});

		// Subscribe to container size changes
		const unsubscribeContainerSize = viewportService.subscribeToContainerSize((newContainerSize) => {
			// console.log('useViewportState: Container size changed via subscription', {
			// 	old: containerSize,
			// 	new: newContainerSize
			// });
			setContainerSize(newContainerSize);
		});

		// Subscribe to viewer size changes
		const unsubscribeViewerSize = viewportService.subscribeToViewerSize((newViewerSize) => {
			// console.log('useViewportState: Viewer size changed via subscription', {
			// 	old: viewerSize,
			// 	new: newViewerSize
			// });
			setViewerSize(newViewerSize);
		});

		// Always subscribe to animation state changes, regardless of manageRenderCallback
		// This ensures controls can detect when animations finish
		const animationStateCallback = () => {
			// Prevent infinite loops by checking if we're already updating
			if (isUpdating.current) {
				return;
			}

			isUpdating.current = true;

			try {
				// Only update states if they've actually changed to prevent infinite loops
				const newIsZooming = viewportService.isZooming;
				const newIsRotating = viewportService.isRotating;
				const newIsPanning = viewportService.isPanning;
				const newContainerSize = viewportService.containerSize;
				const newViewerSize = viewportService.viewerSize;

				// Debug logging for container size updates
				// console.log('useViewportState: Render callback triggered', {
				// 	currentContainerSize: containerSize,
				// 	newContainerSize,
				// 	hasChanged: containerSize !== newContainerSize,
				// 	state: viewportService.state
				// 	config: viewportService.config
				// });

				// Use functional updates to prevent unnecessary re-renders
				setIsZooming((prev) => (prev !== newIsZooming ? newIsZooming : prev));
				setIsRotating((prev) => (prev !== newIsRotating ? newIsRotating : prev));
				setIsPanning((prev) => (prev !== newIsPanning ? newIsPanning : prev));

				// More robust container size comparison - only update if actually different
				setContainerSize((prev) => {
					const hasChanged = prev.width !== newContainerSize.width || prev.height !== newContainerSize.height;
					// console.log('useViewportState: Container size comparison', {
					// 	prev,
					// 	new: newContainerSize,
					// 	hasChanged
					// });
					return hasChanged ? newContainerSize : prev;
				});

				// Only update viewer size if it's actually different
				setViewerSize((prev) => {
					if (!prev && newViewerSize) return newViewerSize;
					if (prev && !newViewerSize) return newViewerSize;
					if (prev && newViewerSize) {
						const hasChanged = prev.width !== newViewerSize.width ||
										prev.height !== newViewerSize.height ||
										prev.x !== newViewerSize.x ||
										prev.y !== newViewerSize.y;
						return hasChanged ? newViewerSize : prev;
					}
					return prev;
				});
			} finally {
				// Reset the flag after a short delay to allow the next update
				setTimeout(() => {
					isUpdating.current = false;
				}, 16); // ~60fps throttle
			}
		};

		// Set the render callback to update animation states
		// console.log('useViewportState: Setting render callback');
		viewportService.setRenderCallback(animationStateCallback);

		// Initial sync - ensure hook state matches service state
		const syncInitialState = () => {
			const currentContainerSize = viewportService.containerSize;
			const currentViewerSize = viewportService.viewerSize;

			// console.log('useViewportState: Initial state sync', {
			// 	hookContainerSize: containerSize,
			// 	serviceContainerSize: currentContainerSize,
			// 	hookViewerSize: viewerSize,
			// 	serviceViewerSize: currentViewerSize
			// });

			// Sync container size if different
			if (currentContainerSize && currentContainerSize.width > 0 && currentContainerSize.height > 0) {
				if (containerSize.width !== currentContainerSize.width || containerSize.height !== currentContainerSize.height) {
					// console.log('useViewportState: Syncing container size on mount');
					setContainerSize(currentContainerSize);
				}
			}

			// Sync viewer size if different
			if (currentViewerSize && currentViewerSize.width > 0 && currentViewerSize.height > 0) {
				if (!viewerSize || viewerSize.width !== currentViewerSize.width || viewerSize.height !== currentViewerSize.height) {
					// console.log('useViewportState: Syncing viewer size on mount');
					setViewerSize(currentViewerSize);
				}
			}
		};

		// Run initial sync after a short delay to ensure service is ready
		const initialSyncTimer = setTimeout(syncInitialState, 50);

		return () => {
			clearTimeout(initialSyncTimer);
			unsubscribeState();
			unsubscribeContainerSize();
			unsubscribeViewerSize();
			// Clear the render callback when unmounting
			viewportService.setRenderCallback(() => {
				// No-op callback to clear the previous one
			});
		};
	}, [viewportService]);

	return { viewportState, isZooming, isRotating, isPanning, containerSize, viewerSize, viewBox: viewportService.getViewBox() };
};
